import { useProductsBySlug } from '@gik/api/products/productsBySlug';
import { useSavedProductIds } from '@gik/api/users/savedProducts';
import { removeFavoriteProduct } from '@gik/api/users/user';
import routes from '@gik/core/routes';
import { useUserStore } from '@gik/core/store/UserStore';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import type { Product } from '@gik/models/gik/Product';
import { CheckoutType } from '@gik/models/gik/Product';
import { Button } from '@gik/ui-legacy/Button';
import { Wishlist } from '@gik/ui-legacy/SvgIcon/GikIcons/Wishlist';
import { SvgIcon } from '@gik/ui-legacy/SvgIcon/SvgIcon';
import { ProductTile } from '@gik/ui-legacy/gik/ProductTile';
import SavedItems from '@gik/user-profile/components/SavedItems/SavedItems';
import { SavedItemsEmpty } from '@gik/user-profile/components/SavedItemsEmpty/SavedItemsEmpty';
import { SavedItemsGrid } from '@gik/user-profile/components/SavedItemsGrid/SavedItemsGrid';
import HeartIcon from '@heroicons/react/outline/HeartIcon';
import TrashIcon from '@heroicons/react/outline/TrashIcon';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { translationKeys } from './i18n/en';

function SavedProductsSectionComp() {
  const { t } = useTranslation();
  const userId = useUserStore(state => state.id);

  const requestKeyIds = `user-${userId}-saved-product-ids`;
  const requestKeyItems = `user-${userId}-saved-products`;

  return (
    <SavedItems
      sectionTitle={t(translationKeys.title)}
      buttonTitle={t(translationKeys.btnSeeAll)}
      buttonPrepend={<SvgIcon Icon={Wishlist} />}
      buttonLink={routes.give}
    >
      <SavedItemsGrid<Product>
        itemName={'product'}
        optimisticIdsComparer={product => id => id !== product.id.toString()}
        optimisticItemsComparer={product => item => item.id != product.id}
        removeCall={async product => await removeFavoriteProduct(userId, product.id)}
        skeleton={<ProductTile direction="row" skeleton />}
        emptyView={
          <SavedItemsEmpty>
            Click the <SvgIcon Icon={HeartIcon} /> on Products to save them here.
          </SavedItemsEmpty>
        }
        requestKeyIds={requestKeyIds}
        requestKeyItems={requestKeyItems}
        fetchIds={function useFetch() {
          return useSavedProductIds(userId, { requestKey: requestKeyIds });
        }}
        fetchItems={function useFetch(ids) {
          return useProductsBySlug({ ids }, { requestKey: requestKeyItems });
        }}
        tileMapper={(product, onClickRemove) => (
          <ProductTile
            key={product.slug}
            title={product.name}
            href={'/products/' + product.slug}
            imageUrl={product.gridImage}
            containImage={
              product.checkoutType === CheckoutType.TangoCard ||
              product.checkoutType === CheckoutType.Perfectgift ||
              product.checkoutType === CheckoutType.GiftyaPlatform
            }
            direction="row"
            toolbar={
              <Button
                circle
                variant={'danger-plain'}
                size={'sm'}
                onClick={async e => {
                  e?.preventDefault();
                  e?.stopPropagation();
                  await onClickRemove(product);
                }}
                tooltip="Remove product"
              >
                {<SvgIcon size="sm" Icon={TrashIcon} />}
              </Button>
            }
          />
        )}
      />
    </SavedItems>
  );
}

export const SavedProductsSection = withComponentErrorBoundary(SavedProductsSectionComp);
